// Single source of truth for reading the device position.
//
// The browser reports three distinct failures — permission denied, position
// unavailable, and timeout — and the difference matters a lot to us: only the
// first is the user's fault. Collapsing them (as the geofence and login gates
// used to) tells someone who already granted location to "turn on location
// access", which they cannot act on. Every caller gets a typed `reason` so the
// UI can say something true.
//
// Resolves (never rejects) with:
//   { ok: true,  coords: { latitude, longitude, accuracy } }
//   { ok: false, reason: "unsupported" | "insecure" | "denied" | "unavailable" | "timeout" }

const CODE_REASON = {
  1: "denied",       // PERMISSION_DENIED
  2: "unavailable",  // POSITION_UNAVAILABLE
  3: "timeout",      // TIMEOUT
};

// A single getCurrentPosition call wrapped as a resolving promise.
function request(options) {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          ok: true,
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
        }),
      (err) => resolve({ ok: false, reason: CODE_REASON[err?.code] || "unavailable" }),
      options,
    );
  });
}

/**
 * Read the current position.
 *
 * @param {object}  opts
 * @param {boolean} opts.highAccuracy  Wake the GPS. Use for one-off, user-initiated
 *   reads. Leave false for background polling — a geofence is a 100m+ radius, so
 *   wifi/cell positioning is plenty and it resolves indoors, where GPS times out.
 * @param {number}  opts.timeout       First-attempt budget in ms.
 * @param {number}  opts.maximumAge    How stale a cached fix may be, in ms.
 */
export async function getPosition({
  highAccuracy = true,
  timeout = 10000,
  maximumAge = 60000,
} = {}) {
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    return { ok: false, reason: "unsupported" };
  }
  // Browsers silently auto-deny geolocation on insecure origins — the API
  // object still exists, the calls just always fail, which reads as a denial.
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return { ok: false, reason: "insecure" };
  }

  // Ask the Permissions API first. When the state is already "denied" the
  // browser shows no prompt and getCurrentPosition just burns the full timeout,
  // so short-circuit and let the caller explain how to unblock instead.
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    if (status.state === "denied") return { ok: false, reason: "denied" };
  } catch {
    // Permissions API unavailable (older Safari) — fall through and request;
    // the error callback still surfaces a real denial.
  }

  const first = await request({ enableHighAccuracy: highAccuracy, timeout, maximumAge });
  if (first.ok || first.reason === "denied") return first;

  // Timed out or no fix. Retry once, coarse: network positioning works indoors
  // where GPS can't see sky, and a several-minute-old fix is fine for deciding
  // which side of a geofence someone is on. This retry is what stops most of
  // the bogus "you denied location" lockouts.
  return request({ enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 });
}

/** True when the failure is something the user must fix in settings. */
export const isPermissionReason = (reason) =>
  reason === "denied" || reason === "insecure" || reason === "unsupported";

// User-facing one-liners, shared by the login gate and the geofence lockout so
// the two never drift apart.
export function locationErrorMessage(reason) {
  switch (reason) {
    case "denied":
      return "Location access is blocked. Allow it in your device settings, then try again.";
    case "insecure":
      return "Location needs a secure (https) connection. Contact your administrator.";
    case "unsupported":
      return "This device or browser can't report a location.";
    default:
      return "We couldn't get a location fix. Move near a window or step outside, make sure Location is on, then try again.";
  }
}
